import type { TokenCredential } from "@azure/identity";
import { AIProjectClient, type HostedAgentDefinition, type PromptAgentDefinition, type ReasoningEffort } from "@azure/ai-projects";
import { authedJson, authedJsonResult } from "./http.js";
import { LocalCliError } from "./errors.js";
import { ensureAgentEndpointEntraIsolation } from "./foundry-agent-version.js";
import type { ProductVisibility } from "@m8t-stack/api-contract";

const METADATA_SOURCE = "m8t";

const FOUNDRY_SCOPE = "https://ai.azure.com/.default";
const API = "v1";

/**
 * Foundry's per-value cap on agent metadata. Exceeding it fails the whole
 * createVersion call with HTTP 400 `invalid_payload`, not just the one key.
 */
export const METADATA_VALUE_MAX = 512;

/**
 * True when a metadata value is small enough for Foundry to accept.
 *
 * Measured in UTF-8 BYTES rather than UTF-16 code units: an accented or CJK
 * founder name costs more than one byte per character, and the two counts
 * disagree exactly for the founders most likely to have long names. Erring
 * toward omission is safe; erring the other way reinstates the 400.
 */
export function fitsAgentMetadataValue(value: string): boolean {
  return Buffer.byteLength(value, "utf8") <= METADATA_VALUE_MAX;
}

export interface CoderMetadata {
  source: "m8t";
  kind: "hosted";
  persona: string;
  personaVersion: string | null;
  [key: string]: string | null; // merge-preserved unmanaged keys (a2a, brain, …)
}

/**
 * Create a hosted coding-agent version via the @azure/ai-projects SDK
 * (the proven TS path). Returns the new version id (e.g. "1").
 *
 * NOTE: the SDK's HostedAgentDefinition uses snake_case fields
 * (`container_protocol_versions` / `environment_variables`), matching the
 * live wire shape observed. Returns the version as a string.
 */
export async function createCoderVersion(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  image: string;
  cpu: string;
  memory: string;
  env: Record<string, string>;
  metadata: CoderMetadata;
  brain?: string;
}): Promise<string> {
  const project = new AIProjectClient(args.endpoint, args.credential);

  const definition: HostedAgentDefinition = {
    kind: "hosted",
    image: args.image,
    cpu: args.cpu,
    memory: args.memory,
    container_protocol_versions: [{ protocol: "responses", version: "1.0.0" }],
    environment_variables: args.env,
  };

  // Spread the caller's full metadata map first so unmanaged keys (a2a,
  // a2aCard, a2aBearerHash, a stale brain, …) reach the real API payload —
  // deploy.ts's redeploy merge is only meaningful if it survives this call.
  // Null-valued keys (personaVersion when absent) are dropped from the spread
  // since the payload is string-only; the branch below re-adds personaVersion
  // when this call's value is non-null. The standard fields are re-asserted
  // over the spread so they always win over a same-named stray key, and the
  // explicit `brain` ARG is applied LAST so it always wins over a stale
  // `brain` key that got spread in from a prior deploy's metadata.
  const metadata: Record<string, string> = {
    ...(Object.fromEntries(Object.entries(args.metadata).filter(([, v]) => v !== null)) as Record<string, string>),
    source: args.metadata.source,
    kind: args.metadata.kind,
    persona: args.metadata.persona,
  };
  if (args.metadata.personaVersion !== null) metadata.personaVersion = args.metadata.personaVersion;
  if (args.brain) metadata.brain = args.brain;

  const version = await project.agents.createVersion(args.name, definition, {
    foundryFeatures: "HostedAgents=V1Preview",
    metadata,
  });

  // The agent exists on the service once createVersion resolves. Bare-API
  // version posts come up with an Entra authorization scheme missing
  // isolation_key_source, which crashes @azure/ai-projects' deserializer on
  // every later agents.get ("Cannot read properties of undefined (reading
  // 'kind')") — heal it now, before the client-side version-field check
  // below, so it still runs even if that check throws. Hosted operations
  // carry the same preview header as the create call above.
  await ensureAgentEndpointEntraIsolation({
    credential: args.credential,
    projectEndpoint: args.endpoint,
    agentName: args.name,
    extraHeaders: { "Foundry-Features": "HostedAgents=V1Preview" },
  });

  const v = (version as { version?: string | number }).version;
  if (v === undefined) {
    throw new LocalCliError({
      code: "CREATE_VERSION_NO_VERSION",
      message: `createVersion for '${args.name}' returned no version id.`,
    });
  }
  return String(v);
}

/** Read the per-version agent identity OID (instance_identity.principal_id) via REST. */
export async function getAgentIdentityPrincipalId(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  version: string;
}): Promise<string> {
  const url = `${args.endpoint}/agents/${args.name}/versions/${args.version}?api-version=${API}`;
  const data = await authedJson<{ instance_identity?: { principal_id?: string } }>({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "GET",
    url,
  });
  const oid = data?.instance_identity?.principal_id;
  if (!oid) {
    throw new LocalCliError({
      code: "AGENT_IDENTITY_MISSING",
      message: `Could not read the agent identity for '${args.name}' version ${args.version}.`,
      hint: "The hosted-agent version may not have provisioned its identity yet; retry in a few seconds.",
    });
  }
  return oid;
}

/** Poll target: the version's status, lowercased ("creating" | "active" | "failed" | ...). */
export async function getVersionStatus(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  version: string;
}): Promise<string> {
  const url = `${args.endpoint}/agents/${args.name}/versions/${args.version}?api-version=${API}`;
  const data = await authedJson<{ status?: string }>({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "GET",
    url,
  });
  return (data?.status ?? "").toLowerCase();
}

export async function agentExists(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
}): Promise<boolean> {
  const url = `${args.endpoint}/agents/${args.name}?api-version=${API}`;
  const res = await authedJsonResult({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "GET",
    url,
    okStatuses: [404],
  });
  return res.status === 200;
}

export interface PromptMetadata {
  persona: string;
  personaVersion: string | null;
  /** JSON-serialized map of resolved fillable-field values (defaults + overrides), for later recovery by `platform update`. */
  fillableFieldValues?: string;
  /**
   * Whether this agent belongs in the product roster. REQUIRED, not optional:
   * the web derives visibility from this key and fails closed, so an agent
   * deployed without it is invisible in the product. Making it required means
   * a new caller cannot omit it by accident — the compiler asks the question.
   */
  productVisibility: ProductVisibility;
}

/**
 * Create a PROMPT agent version via the @azure/ai-projects SDK — the prompt
 * sibling of createCoderVersion. No `foundryFeatures` (that's hosted-only).
 * Used by the intake agent's fast deploy; reusable by the advisor deploys.
 */
export async function createPromptVersion(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
  model: string;
  instructions: string;
  reasoningEffort?: ReasoningEffort;
  /** Persona-declared tools, forwarded verbatim into the definition. Omitted when the
   *  persona declares none, so a tool-less agent stays tool-less rather than gaining an
   *  empty array. */
  tools?: Record<string, unknown>[];
  metadata: PromptMetadata;
  /** Human-facing progress channel. Pass only in human output modes — see the
   *  metadata-cap branch below for why this must not default to stderr. */
  onNotice?: (msg: string) => void;
}): Promise<string> {
  const project = new AIProjectClient(args.endpoint, args.credential);

  const definition: PromptAgentDefinition = {
    kind: "prompt",
    model: args.model,
    instructions: args.instructions,
    ...(args.reasoningEffort ? { reasoning: { effort: args.reasoningEffort } } : {}),
    // Double cast is deliberate and honest: these come straight from persona YAML, so
    // their real static type IS unknown. Modelling the SDK's ToolUnion here would mean a
    // new Foundry tool type could not be declared in a persona without a CLI release —
    // the courier must not become a gatekeeper. Foundry validates the shape and rejects a
    // malformed tool at createVersion, which is the right place for that check.
    ...(args.tools ? { tools: args.tools as unknown as PromptAgentDefinition["tools"] } : {}),
  };

  const metadata: Record<string, string> = {
    source: METADATA_SOURCE,
    kind: "prompt",
    persona: args.metadata.persona,
    // Read by the web app to decide roster membership. Written on EVERY version:
    // createVersion replaces version metadata wholesale rather than merging the
    // previous version's, so omitting it here silently un-publishes an agent on
    // its next re-deploy.
    productVisibility: args.metadata.productVisibility,
  };
  if (args.metadata.personaVersion !== null) metadata.personaVersion = args.metadata.personaVersion;
  if (args.metadata.fillableFieldValues !== undefined) {
    // Foundry rejects the whole request when any metadata VALUE exceeds the cap, so an
    // agent whose composed field values are long would fail to deploy at all. The values
    // are a convenience for later re-render recovery — never worth failing a deploy over.
    //
    // OMIT, never truncate: a clipped JSON string is malformed, and the reader
    // (resolveFillableValues) would throw it away after a failed parse anyway. Absence is
    // a supported path — readers fall back to the local agent yaml, then to the persona's
    // frontmatter defaults. The instructions carry the full values regardless; they are
    // not metadata and are not capped.
    if (fitsAgentMetadataValue(args.metadata.fillableFieldValues)) {
      metadata.fillableFieldValues = args.metadata.fillableFieldValues;
    } else {
      // Reported through the caller's own progress channel, never written straight to
      // stderr: `m8t agent deploy-advisor --output json` runs inside the installer's
      // retry_classified wrapper, which jq-parses the captured stream, and one stray
      // line there makes a transient failure read as non-retryable (installer/lib.sh).
      // Callers pass a progress fn only in human output modes, so this stays silent
      // exactly where a machine is reading.
      args.onNotice?.(
        `field values (${String(Buffer.byteLength(args.metadata.fillableFieldValues, "utf8"))} bytes) ` +
        `exceed Foundry's ${String(METADATA_VALUE_MAX)}-byte metadata limit — omitting them from ` +
        `'${args.name}' metadata; the agent's instructions are unaffected.`,
      );
    }
  }

  const version = await project.agents.createVersion(args.name, definition, { metadata });

  // Same endpoint-auth healing as createCoderVersion above — no preview
  // header here since prompt agents aren't hosted.
  await ensureAgentEndpointEntraIsolation({
    credential: args.credential,
    projectEndpoint: args.endpoint,
    agentName: args.name,
  });

  const v = (version as { version?: string | number }).version;
  if (v === undefined) {
    throw new LocalCliError({
      code: "CREATE_VERSION_NO_VERSION",
      message: `createVersion for '${args.name}' returned no version id.`,
    });
  }
  return String(v);
}

/**
 * Delete the agent (cascades the container + its identity + role assignment).
 * `force=true` also cascade-deletes active sessions — without it the platform
 * 409s ("Agent has active sessions") for ~15 min after a recent invoke. A
 * teardown is deliberate + confirm-gated, so forcing is the right behavior.
 */
export async function deleteAgent(args: {
  credential: TokenCredential;
  endpoint: string;
  name: string;
}): Promise<void> {
  const url = `${args.endpoint}/agents/${args.name}?api-version=${API}&force=true`;
  await authedJsonResult({
    credential: args.credential,
    scope: FOUNDRY_SCOPE,
    method: "DELETE",
    url,
    okStatuses: [404], // already gone — idempotent
  });
}
